import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTour } from '../context/TourContext';
import { Box, Paper, Typography, Button, IconButton, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

const TourOverlay = () => {
  const { activeTour, currentStep, stepIndex, totalSteps, nextStep, prevStep, stopTour } = useTour();
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const retryTimeout = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isPositioning, setIsPositioning] = useState(false);

  // Function to find target and calculate positions
  const findAndPositionTarget = useCallback(() => {
    // Safety check
    if (!activeTour || !currentStep || !currentStep.selector) {
      setIsVisible(false);
      setTargetRect(null);
      return;
    }

    // Find all elements matching selector
    const elements = document.querySelectorAll(currentStep.selector);
    
    // Filter for visible elements (offsetParent !== null means visible in DOM)
    const visibleElements = Array.from(elements).filter(el => el.offsetParent !== null);
    
    // Use the first visible element found
    const element = visibleElements[0];
    
    if (!element) {
      // Element not visible yet - retry
      clearTimeout(retryTimeout.current);
      retryTimeout.current = setTimeout(findAndPositionTarget, 300);
      return;
    }

    const rect = element.getBoundingClientRect();
    
    // Ensure element is in viewport
    if (rect.top < -100 || rect.bottom > window.innerHeight + 100) {
      // Element might be scrolled out - retry
      clearTimeout(retryTimeout.current);
      retryTimeout.current = setTimeout(findAndPositionTarget, 300);
      return;
    }

    setTargetRect(rect);
    setIsVisible(true);

    // Calculate tooltip position based on placement
    const placement = currentStep.placement || 'bottom';
    let top, left;

    switch (placement) {
      case 'bottom':
        top = rect.bottom + 20;
        left = Math.min(Math.max(rect.left + rect.width / 2 - 180, 20), window.innerWidth - 380);
        break;
      case 'top':
        top = rect.top - 20 - 200;
        left = Math.min(Math.max(rect.left + rect.width / 2 - 180, 20), window.innerWidth - 380);
        break;
      case 'left':
        top = Math.min(Math.max(rect.top + rect.height / 2 - 100, 20), window.innerHeight - 220);
        left = rect.left - 20 - 380;
        break;
      case 'right':
        top = Math.min(Math.max(rect.top + rect.height / 2 - 100, 20), window.innerHeight - 220);
        left = rect.right + 20;
        break;
      default:
        top = rect.bottom + 20;
        left = Math.min(Math.max(rect.left + rect.width / 2 - 180, 20), window.innerWidth - 380);
    }

    // Ensure tooltip stays in viewport
    if (left < 20) left = 20;
    if (left + 360 > window.innerWidth - 20) left = window.innerWidth - 380;
    if (top < 20) top = 20;
    if (top + 200 > window.innerHeight - 20) top = window.innerHeight - 220;

    setTooltipPosition({ top, left });
    setIsPositioning(false);
  }, [activeTour, currentStep]);

  // Initial positioning
  useEffect(() => {
    if (!activeTour || !currentStep) {
      setIsVisible(false);
      setTargetRect(null);
      return;
    }

    setIsPositioning(true);
    // Wait for DOM to update after navigation
    const timeout = setTimeout(findAndPositionTarget, 300);
    
    return () => {
      clearTimeout(timeout);
      clearTimeout(retryTimeout.current);
    };
  }, [activeTour, currentStep, findAndPositionTarget]);

  // Handle scroll events - reposition the target
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      // Don't reposition if we're already positioning
      if (isPositioning) return;
      
      setIsPositioning(true);
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        findAndPositionTarget();
      });
    };

    // Throttle scroll events for performance
    let scrollTimeout;
    const throttledScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    };

    window.addEventListener('scroll', throttledScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', throttledScroll, true);
      window.removeEventListener('resize', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isVisible, isPositioning, findAndPositionTarget]);

  // CRITICAL SAFETY CHECK - If no currentStep or no activeTour, render nothing
  if (!activeTour || !currentStep || !isVisible || !targetRect) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {/* Spotlight cutout - click-through */}
      <Box
        sx={{
          position: 'absolute',
          top: targetRect.top - 12,
          left: targetRect.left - 12,
          width: targetRect.width + 24,
          height: targetRect.height + 24,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          borderRadius: '12px',
          pointerEvents: 'none',
          transition: 'all 0.15s ease-out',
          border: '2px solid #009db9',
        }}
      />

      {/* Highlight glow effect */}
      <Box
        sx={{
          position: 'absolute',
          top: targetRect.top - 16,
          left: targetRect.left - 16,
          width: targetRect.width + 32,
          height: targetRect.height + 32,
          borderRadius: '16px',
          boxShadow: '0 0 30px rgba(0, 157, 185, 0.3)',
          pointerEvents: 'none',
          transition: 'all 0.15s ease-out',
          border: '2px solid rgba(0, 157, 185, 0.3)',
        }}
      />

      {/* Tooltip card */}
      <Fade in={true} timeout={300}>
        <Paper
          sx={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: 360,
            maxWidth: '90vw',
            p: 3,
            pointerEvents: 'all',
            zIndex: 10000,
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 157, 185, 0.2)',
            transition: 'top 0.15s ease-out, left 0.15s ease-out',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" fontWeight={600} color="#009db9">
              {currentStep.title}
            </Typography>
            <IconButton 
              onClick={stopTour} 
              size="small"
              sx={{
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={3} sx={{ lineHeight: 1.6 }}>
            {currentStep.content}
          </Typography>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Step {stepIndex + 1} of {totalSteps}
            </Typography>
            <Box display="flex" gap={1}>
              {stepIndex > 0 && (
                <Button
                  onClick={prevStep}
                  size="small"
                  startIcon={<NavigateBeforeIcon />}
                  sx={{
                    color: '#009db9',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 157, 185, 0.08)',
                    }
                  }}
                >
                  Back
                </Button>
              )}
              {stepIndex < totalSteps - 1 ? (
                <Button
                  variant="contained"
                  onClick={nextStep}
                  size="small"
                  endIcon={<NavigateNextIcon />}
                  sx={{
                    backgroundColor: '#009db9',
                    '&:hover': {
                      backgroundColor: '#007a92',
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={stopTour}
                  size="small"
                  sx={{
                    backgroundColor: '#009db9',
                    '&:hover': {
                      backgroundColor: '#007a92',
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Finish 🎉
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default TourOverlay;